import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import dayjs from 'dayjs';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import SmsOutlinedIcon from '@mui/icons-material/SmsOutlined';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import RefreshIcon from '@mui/icons-material/Refresh';
import AuthContext from '../../context/authContext';
import useIssue from '../../hooks/useIssue';
import {
  ESCALATION_RUN_LABELS,
  ESCALATION_STEP_LABELS,
  ISSUE_PRIORITY_BY_KEY,
  formatDurationMs,
} from '../../util/issueConstants';

const DATE_FORMAT = 'DD.MM.YYYY HH:mm';

const RUN_STATUS_COLOR = {
  running: 'warning',
  acknowledged: 'success',
  exhausted: 'error',
  cancelled: 'default',
};

const STEP_STATUS_CONFIG = {
  sent: { label: 'Gesendet', color: 'success', variant: 'filled' },
  scheduled: { label: 'Geplant', color: 'default', variant: 'filled' },
  skipped: { label: 'Übersprungen', color: 'warning', variant: 'outlined' },
  failed: { label: 'Fehlgeschlagen', color: 'error', variant: 'filled' },
};

// Compact location caption for a run's issue: branch · entertainment · resource.
const issueLocation = issue =>
  [
    issue?.branchId?.locationName || issue?.branchId?.longName,
    issue?.entertainmentId?.entertainmentName || issue?.entertainmentId?.longName,
    issue?.resourceId?.name,
  ]
    .filter(Boolean)
    .join(' · ');

export default function EscalationAuditView() {
  const authCtx = useContext(AuthContext);
  const navigate = useNavigate();
  const { getEscalationRuns } = useIssue();

  const canRead = authCtx.checkRight('issue:manage') || authCtx.checkRight('issue:statistics');

  const [runs, setRuns] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getEscalationRuns();
      if (res?.runs) setRuns(res.runs);
    } catch {
      // Error toast is emitted by the hook.
    } finally {
      setLoading(false);
    }
  }, [getEscalationRuns]);

  useEffect(() => {
    if (!canRead) return;
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canRead]);

  const sortedRuns = useMemo(
    () =>
      [...runs].sort(
        (a, b) => dayjs(b.meta?.createdAt).valueOf() - dayjs(a.meta?.createdAt).valueOf()
      ),
    [runs]
  );

  if (!canRead) return <Navigate to='/' replace />;

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, color: 'text.primary' }}>
      <Stack
        direction='row'
        alignItems='center'
        justifyContent='space-between'
        flexWrap='wrap'
        gap={2}
      >
        <Box>
          <Typography variant='h5' fontWeight={700}>
            Eskalations-Audit
          </Typography>
          <Typography variant='body2' color='text.secondary'>
            Rückblick über alle Eskalationsketten: wer wurde wann informiert und wer hat reagiert.
          </Typography>
        </Box>
        <Tooltip title='Aktualisieren'>
          <IconButton onClick={load} disabled={loading}>
            <RefreshIcon />
          </IconButton>
        </Tooltip>
      </Stack>

      {loading && runs.length === 0 ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress />
        </Box>
      ) : sortedRuns.length === 0 ? (
        <Alert severity='info' sx={{ mt: 3 }}>
          Keine Eskalationsketten vorhanden.
        </Alert>
      ) : (
        <Box sx={{ mt: 3 }}>
          {sortedRuns.map(run => {
            const priority = run.issueId ? ISSUE_PRIORITY_BY_KEY[run.issueId.priority] : null;
            const createdAt = run.meta?.createdAt;
            const ackDurationMs =
              run.acknowledgedAt && createdAt
                ? dayjs(run.acknowledgedAt).diff(dayjs(createdAt))
                : null;
            const location = run.issueId ? issueLocation(run.issueId) : '';
            return (
              <Accordion key={run._id} disableGutters>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Stack
                    direction='row'
                    alignItems='center'
                    spacing={1.5}
                    flexWrap='wrap'
                    useFlexGap
                    sx={{ width: '100%', pr: 1 }}
                  >
                    <Typography variant='body2' color='text.secondary' sx={{ minWidth: 130 }}>
                      {createdAt ? dayjs(createdAt).format(DATE_FORMAT) : '—'}
                    </Typography>
                    {priority && (
                      <Chip
                        size='small'
                        label={priority.shortLabel}
                        sx={{
                          backgroundColor: alpha(priority.color, 0.15),
                          color: priority.color,
                          fontWeight: 700,
                        }}
                      />
                    )}
                    <Box sx={{ minWidth: 0, flexGrow: 1 }}>
                      <Typography variant='body2' fontWeight={600} noWrap>
                        {run.issueId?.title || 'Issue nicht mehr vorhanden'}
                      </Typography>
                      {location && (
                        <Typography variant='caption' color='text.secondary' noWrap display='block'>
                          {location}
                        </Typography>
                      )}
                    </Box>
                    <Chip
                      size='small'
                      color={RUN_STATUS_COLOR[run.status] || 'default'}
                      label={ESCALATION_RUN_LABELS[run.status] || run.status}
                    />
                    {run.status === 'acknowledged' && run.acknowledgedByUsername && (
                      <Typography variant='caption' color='success.main'>
                        Reagiert von {run.acknowledgedByUsername} nach{' '}
                        {formatDurationMs(ackDurationMs)}
                      </Typography>
                    )}
                  </Stack>
                </AccordionSummary>
                <AccordionDetails>
                  <Box sx={{ overflowX: 'auto' }}>
                    <Table size='small'>
                      <TableHead>
                        <TableRow>
                          <TableCell>#</TableCell>
                          <TableCell>Kanal</TableCell>
                          <TableCell>Stufe</TableCell>
                          <TableCell>Empfänger</TableCell>
                          <TableCell>Geplant</TableCell>
                          <TableCell>Gesendet</TableCell>
                          <TableCell>Status</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {(run.steps || [])
                          .slice()
                          .sort((a, b) => (a.order || 0) - (b.order || 0))
                          .map((step, idx) => {
                            const statusConfig =
                              STEP_STATUS_CONFIG[step.status] || STEP_STATUS_CONFIG.scheduled;
                            return (
                              <TableRow key={idx}>
                                <TableCell>{step.order}</TableCell>
                                <TableCell>
                                  <Tooltip title={step.kind === 'sms' ? 'SMS' : 'E-Mail'}>
                                    {step.kind === 'sms' ? (
                                      <SmsOutlinedIcon fontSize='small' />
                                    ) : (
                                      <MailOutlineIcon fontSize='small' />
                                    )}
                                  </Tooltip>
                                </TableCell>
                                <TableCell>
                                  {ESCALATION_STEP_LABELS[step.reasonTag] || step.reasonTag || '—'}
                                </TableCell>
                                <TableCell>{step.username || '—'}</TableCell>
                                <TableCell>
                                  {step.scheduledFor
                                    ? dayjs(step.scheduledFor).format(DATE_FORMAT)
                                    : '—'}
                                </TableCell>
                                <TableCell>
                                  {step.sentAt ? dayjs(step.sentAt).format(DATE_FORMAT) : '—'}
                                </TableCell>
                                <TableCell>
                                  <Chip
                                    size='small'
                                    color={statusConfig.color}
                                    variant={statusConfig.variant}
                                    label={statusConfig.label}
                                  />
                                  {(step.skippedReason || step.error) && (
                                    <Typography
                                      variant='caption'
                                      color='text.secondary'
                                      display='block'
                                    >
                                      {step.skippedReason || step.error}
                                    </Typography>
                                  )}
                                </TableCell>
                              </TableRow>
                            );
                          })}
                      </TableBody>
                    </Table>
                  </Box>
                  {run.issueId && (
                    <Button
                      size='small'
                      startIcon={<OpenInNewIcon />}
                      onClick={() => navigate(`/issues?issueId=${run.issueId._id}`)}
                      sx={{ mt: 1 }}
                    >
                      Zum Issue
                    </Button>
                  )}
                </AccordionDetails>
              </Accordion>
            );
          })}
        </Box>
      )}
    </Box>
  );
}
